/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { Spinner } from "@chakra-ui/react";
import Layout from "layouts/layout";
import PublicLayout from "layouts/public";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import useRoutes from "../routes";
import NotFound from "../views/public/NotFound";
import { Auth } from "aws-amplify";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import Footer from "components/footer/FooterAuthDefault";
import { ProfileContext } from "../store/ProfileProvider";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

type Route = {
  isSideBar: boolean;
  layout: string;
  name: string;
  path: string;
};

const appName = process.env.REACT_APP_NAME!;
const theme = process.env.REACT_APP_THEME || "default";

const AUTH_VERSION = "2.0.0";

export const ProtectedRoute = ({ ...rest }) => {
  const {
    isAuthenticated,
    isAuthenticating,
    checkAuthStatus,
    fetchPermissions,
    updateAuthStatus,
  } = useAuth();
  const { profileTimeZone } = useContext(ProfileContext);
  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const isSpecialRoute = ["confirm", "500", "expired", "404", "kyc", "confirming"].includes(pathSegments[0]);

  // Check if current route is a selection screen
  const isAuthSelectionRoute =
    location.pathname === "/auth/merchant-selection" ||
    location.pathname === "/auth/subaccount-selection";

  const userRole = localStorage.getItem("userRole");
  const roleRoute = userRole ? userRole.toLowerCase() : '';
  const merchantId = localStorage.getItem("merchantId");
  const subaccountId = localStorage.getItem("subaccountId");
  const apiToken = localStorage.getItem("apiToken");
  const cognitoId = localStorage.getItem("cognitoId");
  const username = localStorage.getItem("username");
  const userid = localStorage.getItem("userid");

  const [userPermissions, setUserPermissions] = useState([]);
  const [isValidatingSelection, setIsValidatingSelection] = useState(false);
  const { routes } = useRoutes(userPermissions);
  const navigate = useNavigate();

  axios.defaults.headers.common['Authorization'] = 'Bearer ' + apiToken;

  const { pathname } = useLocation();

  useEffect(() => {
    // Public / special routes (confirm, kyc, expired, confirming, 404, 500) are
    // opened by unauthenticated visitors (e.g. receivers clicking an email link)
    // who have never signed in and therefore have no authVersion in localStorage.
    // They must not be forced to sign-in by the guards below.
    if (isSpecialRoute) {
      window.scrollTo(0, 0);
      return;
    }

    const currentAuthVersion = localStorage.getItem('authVersion');
    if (currentAuthVersion !== AUTH_VERSION) {
      console.log(`Auth version mismatch (${currentAuthVersion} vs ${AUTH_VERSION}) - forcing re-authentication`);
      navigateToSignin();
      return;
    }

    // Merchant role must not have subaccountId — invalid state, force sign out
    if (userRole === "Merchant" && subaccountId) {
      console.log("Merchant role should not have subaccountId - forcing re-authentication");
      navigateToSignin();
      return;
    }

    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (
      !pathSegments.includes("confirm") &&
      !pathSegments.includes("404") &&
      !pathSegments.includes("500")
    ) {
      document.title = appName + " | Settlement System";
    }

    const appleTitle = document.querySelector("meta[name='apple-mobile-web-app-title']");
    if (appleTitle) appleTitle.setAttribute("content", appName);

    const manifest = document.querySelector("link[rel='manifest']");
    if (manifest) manifest.setAttribute("href", `/themes/${theme}/site.webmanifest`);

    const apiTokenExpiresFromStorage = localStorage.getItem("apiTokenExpires");
    const lastSignInFromStorage = localStorage.getItem("lastSignIn");

    // Handle selection routes separately
    if (isAuthSelectionRoute) {
      handleSelectionRouteValidation();
      return;
    }

    const getPermissions = async () => {
      const currentUser = await checkAuthStatus();
      if (currentUser) {
        const permissions = await fetchPermissions(currentUser, () => { });
        if (permissions) {
          updateAuthStatus();
          setUserPermissions(permissions);
        } else {
          navigateToSignin();
        }
      } else {
        navigateToSignin();
      }
    };

    const getTokenStatus = async () => {
      if (apiToken) {
        let stilOnTime = true;
        const apiTokenExpires = moment(apiTokenExpiresFromStorage);
        const currentTime = moment();
        const differenceInSeconds = apiTokenExpires.diff(currentTime, 'seconds');
        if (differenceInSeconds <= 3600) {
          extendTokenExpires();
        } else if (differenceInSeconds <= 0) {
          stilOnTime = false;
        }

        if (stilOnTime) {
          getPermissions();
        } else {
          navigateToSignin();
        }
      } else {
        navigateToSignin();
      }
    };

    localStorage.removeItem("startIdleCheck");

    const startIdleCheckId = localStorage.getItem("startIdleCheckId");
    clearInterval(startIdleCheckId);
    localStorage.removeItem("startIdleCheckId");

    const startCountdownId = localStorage.getItem("startIdleCheckId");
    clearInterval(startCountdownId);
    localStorage.removeItem("startCountdownId");

    const date = dayjs.utc(lastSignInFromStorage).tz(profileTimeZone).add(1, 'hour').startOf('day');
    const today = dayjs().tz(profileTimeZone).startOf('day');
    const isPastDate = date.isBefore(today);

    if (
      !pathSegments.includes("confirm") &&
      !pathSegments.includes("404") &&
      !pathSegments.includes("500") &&
      !pathSegments.includes("expired") &&
      !pathSegments.includes("kyc") &&
      !pathSegments.includes("confirming")
    ) {
      if (isPastDate) {
        navigateToSignin();
      } else {
        getTokenStatus();
      }
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticating && !isAuthenticated && !isAuthSelectionRoute && !isSpecialRoute) {
      navigateToSignin();
    }
  }, [isAuthenticating, isAuthenticated]);

  /**
   * Validate selection routes security
   * - Must have Cognito session (checked via Auth.currentAuthenticatedUser)
   * - Must have required localStorage data (cognitoId, username, userid)
   * - Navigation state must exist
   * - On refresh or direct access, redirect to sign-in
   */
  const handleSelectionRouteValidation = async () => {
    setIsValidatingSelection(true);

    try {
      // 1. Check if user came from proper navigation (has state)
      const hasNavigationState = location.state && location.state.cognitoId;

      // 2. Check if required localStorage exists
      const hasRequiredLocalStorage = cognitoId && username && userid;

      // 3. If refresh or direct access (no state), redirect to sign-in
      if (!hasNavigationState) {
        console.log("Selection route: No navigation state - redirecting to sign-in");
        navigateToSignin();
        return;
      }

      // 4. Validate localStorage data exists
      if (!hasRequiredLocalStorage) {
        console.log("Selection route: Missing required localStorage - redirecting to sign-in");
        navigateToSignin();
        return;
      }

      // 5. Verify Cognito session is still valid
      try {
        const cognitoUser = await Auth.currentAuthenticatedUser();
        if (!cognitoUser) {
          console.log("Selection route: No Cognito session - redirecting to sign-in");
          navigateToSignin();
          return;
        }

        // 6. Verify cognitoId matches
        const cognitoIdFromSession = cognitoUser.attributes.sub;
        if (cognitoIdFromSession !== cognitoId) {
          console.log("Selection route: CognitoId mismatch - redirecting to sign-in");
          navigateToSignin();
          return;
        }

        // All validations passed - allow access to selection screen
        setIsValidatingSelection(false);
      } catch (cognitoError) {
        console.log("Selection route: Cognito authentication failed - redirecting to sign-in");
        navigateToSignin();
      }
    } catch (error) {
      console.error("Selection route validation error:", error);
      navigateToSignin();
    }
  };

  const navigateToSignin = async () => {
    try {
      await Auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }

    localStorage.clear();
    localStorage.setItem('authVersion', AUTH_VERSION);
    navigate(`/auth/sign-in`);
  };

  const extendTokenExpires = async () => {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/user/request`,
      {
        fn: "extend-auth"
      }
    );

    let { token, expires } = response.data;

    if (token != null) {
      localStorage.setItem("apiToken", token);
      localStorage.setItem("apiTokenExpires", expires);
    } else {
      navigateToSignin();
    }
  }

  const convertPathToRegex = (routePath: string) => {
    return new RegExp(`^${routePath.replace(/:\w+/g, '[^/]+')}$`);
  };

  const isRouteExist = (url: string, userRoutes: Route[]): boolean => {
    return userRoutes.some(route => {
      const fullPath = `${route.layout}/${route.path}`;
      const regex = convertPathToRegex(fullPath);
      return regex.test(url);
    });
  };

  // Merchant role must not have subaccountId — invalid state, block rendering and force sign out
  if (userRole === "Merchant" && subaccountId) {
    navigateToSignin();
    return null;
  }

  // Handle selection routes with validation
  if (isAuthSelectionRoute) {
    if (isValidatingSelection) {
      return (
        <div className="flex h-full w-full min-h-screen items-center justify-center justify-items-center">
          <div>
            <Spinner className="h-6 w-6 layout-spinner-color" size="xl" />
          </div>
        </div>
      );
    }
    // Validation passed, render selection layout
    return <PublicLayout />;
  }

  if (location.pathname === "/" || location.pathname === "/" + roleRoute || location.pathname === "/" + roleRoute + "/") {
    if (userRole && merchantId && (subaccountId || userRole === "Merchant" || userRole === "SuperAdmin")) {
      return <Navigate to={`/${roleRoute}/dashboard`} replace />;
    } else if (!merchantId) {
      return <Navigate to="/auth/merchant-selection" replace />;
    } else if (!subaccountId) {
      return <Navigate to="/auth/subaccount-selection" replace />;
    }
  } else if (isSpecialRoute) {
    if (location.pathname.includes("/confirming")) {
      if (location.pathname.includes("/confirming/") && location.pathname.split('/')[2] !== "") {
        return <PublicLayout />;
      } else {
        return <Navigate to="/404" replace />;
      }
    } else if (location.pathname.includes("/confirm")) {
      if (location.pathname.includes("/confirm/") && location.pathname.split('/')[2] !== "") {
        return <PublicLayout />;
      } else if (location.pathname.includes("/500")) {
        return <Navigate to="/500" replace />;
      } else {
        return <Navigate to="/404" replace />;
      }
    } else if (location.pathname.includes("/expired")) {
      if (location.pathname.includes("/expired/") && location.pathname.split('/')[2] !== "") {
        return <PublicLayout />;
      } else if (location.pathname.includes("/500")) {
        return <Navigate to="/500" replace />;
      } else {
        return <Navigate to="/404" replace />;
      }
    } else if (location.pathname.includes("/kyc")) {
      if (location.pathname.includes("/kyc/") && location.pathname.split('/')[2] !== "") {
        return <PublicLayout />;
      } else {
        return <Navigate to="/404" replace />;
      }
    } else {
      return <PublicLayout />;
    }
  } else {
    if (isAuthenticated && userRole) {
      if (roleRoute && !location.pathname.startsWith(`/${roleRoute}`)) {
        return <Navigate to={`/${roleRoute}/dashboard`} replace />;
      }

      if (userPermissions) {
        const currentPath = window.location.pathname;
        if (isRouteExist(currentPath, routes[userRole.toLowerCase()])) {
          return <Layout userRole={userRole} userPermissions={userPermissions} />;
        } else {
          return (
            <div>
              <div className="relative h-auto min-h-screen w-full">
                <main className={`mx-auto min-h-screen`}>
                  <div className="relative flex items-center justify-center">
                    <div className="mx-auto flex flex-col pt-12 lg:h-screen lg:px-8 lg:pt-0 xl:h-full xl:min-h-screen xl:px-0">
                      <div className="mb-auto flex flex-col items-center justify-center md:pr-0 lg:pl-0 xl:max-w-full">
                        <NotFound hideFooter={true} isAuthenticated={true} />
                      </div>
                      <Footer />
                    </div>
                  </div>
                </main>
              </div>
            </div>
          );
        }
      } else if (isAuthenticating) {
        return (
          <div className="flex h-full w-full min-h-screen items-center justify-center justify-items-center">
            <div>
              <Spinner className="h-6 w-6 layout-spinner-color" size="xl" />
            </div>
          </div>
        );
      }
    } else if (isAuthenticating) {
      return (
        <div className="flex h-full w-full min-h-screen items-center justify-center justify-items-center">
          <div>
            <Spinner className="h-6 w-6 layout-spinner-color" size="xl" />
          </div>
        </div>
      );
    }
  }

  return null;
};

export default ProtectedRoute;